import { faultOf, type Fault } from './sbmonitoring.ts';
import type { GameEvent } from './sbtelemetry.ts';

/** 浏览器这边每分钟数一次:这一分钟画了几帧、最长一帧多久、长任务、页面在不在前台、引擎线程回话快不快、页面的 JS 内存。
 * 不开城、不推城;引擎线程半分钟以上不回话(页面在前台、没暂停)记一次出错。
 * 页面每次打开在 localStorage 记一笔「开着」(sb:m:run:页面号),正常关掉时删掉;下次打开还看到别的页面的「开着」,
 * 而且那一页超过三分钟没更新,就是上次没正常关(浏览器崩了、标签页被杀、内存不够),报一次。 */
export type BrowserBeat = { ms: number; visMs: number; frames: number; maxGapMs: number; longTasks: number; longTaskMs: number; workerMs?: number; mem?: number; heap?: number; hidden: boolean };
export const PAGE_ID = Math.floor(Math.random() * 0xffffffff) >>> 0;
const RUN_KEY = 'sb:m:run:';

export function installBrowserMonitoring(options: {
  event: (event: GameEvent) => void; day: () => number; paused: () => boolean;
  beat: (browser: BrowserBeat) => void; fault: (fault: Fault) => void;
  ping: (id: number) => void; exit: () => void; retry?: () => void; beatMs?: number;
}) {
  let started = false, disposed = false, startAt = Date.now(), serial = 0, lastPing: { id: number; at: number } | null = null;
  let frames = 0, maxGap = 0, lastFrame = 0, longTasks = 0, longTaskMs = 0, raf = 0, windowAt = Date.now(), visibleAt = Date.now(), visMs = 0;
  let workerMs: number | undefined, mem: number | undefined, stallReported = false, lastMarkMem: number | undefined;
  const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
  const uptimeMs = () => Math.max(0, Math.min(31536000000, Date.now() - startAt));
  const emit = (event: GameEvent) => { if (started && !disposed) options.event(event); };
  const mark = (open: boolean) => {
    try { if (open) localStorage.setItem(RUN_KEY + PAGE_ID, JSON.stringify({ at: Date.now(), day: options.day(), mem: lastMarkMem ?? null })); else localStorage.removeItem(RUN_KEY + PAGE_ID); }
    catch { /* 存不了就不查上次 */ }
  };
  const priorCrashes = () => {
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i); if (!key?.startsWith(RUN_KEY) || key === RUN_KEY + PAGE_ID) continue;
        const prior = JSON.parse(localStorage.getItem(key) ?? 'null') as { at?: number; day?: number; mem?: number | null } | null;
        // 别的标签页还开着的会一分钟更新一次;三分钟没动的才算没正常关
        if (!prior || typeof prior.at !== 'number' || Date.now() - prior.at < 180_000) continue;
        localStorage.removeItem(key);
        if (Date.now() - prior.at > 30 * 86400_000) continue;
        const day = typeof prior.day === 'number' ? prior.day : 0, last = typeof prior.mem === 'number' ? prior.mem : undefined;
        options.fault(faultOf('unclean', `上次没正常关:停在第 ${Math.floor(day / 360) + 1} 年${last ? `,引擎内存 ${Math.round(last / 1e6)} MB` : ''}`, undefined, { mem: last, where: 'startup' }));
      }
    } catch { /* 读不了就算了 */ }
  };
  const visibility = () => { if (!hidden()) visibleAt = Date.now(); };
  const beat = () => {
    if (!started || disposed) return;
    const now = Date.now();
    if (!hidden()) { visMs += now - visibleAt; visibleAt = now; }
    if (lastPing && !hidden() && !options.paused() && now - lastPing.at > 30_000 && !stallReported) {
      stallReported = true; options.fault(faultOf('stall', `引擎线程 ${Math.round((now - lastPing.at) / 1000)} 秒没回话`, undefined, { where: 'step', mem }));
    }
    const heap = (performance as unknown as { memory?: { usedJSHeapSize?: number } }).memory?.usedJSHeapSize;
    try {
      options.beat({ ms: Math.max(0, now - windowAt), visMs: Math.max(0, visMs), frames, maxGapMs: Math.min(3600000, maxGap), longTasks, longTaskMs: Math.min(3600000, longTaskMs),
        ...(workerMs !== undefined ? { workerMs } : {}), ...(mem !== undefined ? { mem } : {}), ...(typeof heap === 'number' && heap > 0 ? { heap } : {}), hidden: hidden() });
    } catch { /* 心跳出错不挡游戏 */ }
    frames = 0; maxGap = 0; longTasks = 0; longTaskMs = 0; windowAt = now; visMs = 0; workerMs = undefined;
    lastMarkMem = mem; mark(true);
    if (!lastPing || lastPing.id < 0) { lastPing = { id: ++serial, at: now }; try { options.ping(serial); } catch { /* Worker 的 error 事件会报 */ } }
  };
  const tick = (time: number) => {
    if (disposed) return;
    if (started && !hidden()) { frames++; if (lastFrame) maxGap = Math.max(maxGap, time - lastFrame); }
    lastFrame = time;
    raf = window.requestAnimationFrame(tick);
  };
  let observer: PerformanceObserver | null = null;
  if (typeof PerformanceObserver !== 'undefined') try {
    observer = new PerformanceObserver(list => { if (!started) return; for (const entry of list.getEntries().slice(0, 128)) { longTasks++; longTaskMs += Math.max(0, entry.duration); } });
    observer.observe({ type: 'longtask', buffered: false });
  } catch { observer = null; }
  if (typeof window.requestAnimationFrame === 'function') raf = window.requestAnimationFrame(tick);
  const timer = setInterval(beat, options.beatMs ?? 60_000);
  const changed = () => { lastFrame = 0; if (hidden()) { visMs += Date.now() - visibleAt; } else visibility(); };
  const exit = () => { beat(); emit({ type: 'lifecycle', day: options.day(), payload: { state: 'close', reason: 'unknown', visibility: hidden() ? 'hidden' : 'visible', uptimeMs: uptimeMs() } }); mark(false); options.exit(); };
  const online = () => options.retry?.();
  document.addEventListener('visibilitychange', changed); window.addEventListener('pagehide', exit); window.addEventListener('online', online);
  return {
    start() {
      const firstStart = !started;
      started = true; startAt = windowAt = visibleAt = Date.now(); frames = longTasks = longTaskMs = maxGap = lastFrame = visMs = 0; lastPing = null; stallReported = false;
      if (firstStart) priorCrashes();
      mark(true); emit({ type: 'lifecycle', day: options.day(), payload: { state: 'start', reason: 'new_city', visibility: hidden() ? 'hidden' : 'visible', uptimeMs: 0 } });
      lastPing = { id: ++serial, at: Date.now() }; try { options.ping(serial); } catch { /* 同上 */ }
    },
    pong(id: number, memoryBytes?: number) {
      if (lastPing?.id !== id) return;
      workerMs = Math.min(3600000, Math.max(0, Date.now() - lastPing.at)); lastPing = null; stallReported = false;
      if (typeof memoryBytes === 'number' && Number.isFinite(memoryBytes) && memoryBytes >= 0) mem = memoryBytes;
    },
    beat,
    dispose() { disposed = true; clearInterval(timer); observer?.disconnect(); if (raf) window.cancelAnimationFrame(raf); document.removeEventListener('visibilitychange', changed); window.removeEventListener('pagehide', exit); window.removeEventListener('online', online); },
  };
}
